import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useLoc } from "@/lib/loc";
import VideoCard from "@/components/VideoCard";
import Seo from "@/components/Seo";
import { Loader2 } from "lucide-react";

export default function Browse() {
  const { t } = useTranslation();
  const loc = useLoc();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";

  const { data: cats } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: videos, isLoading } = useQuery({
    queryKey: ["browse", category],
    queryFn: async () => (await api.get("/videos", { params: category ? { category_id: category } : {} })).data,
  });

  const setCat = (id) => {
    if (id) setParams({ category: id });
    else setParams({});
  };

  return (
    <div className="min-h-screen px-6 pt-24 md:px-12">
      <Seo title={t("browse.title")} description={t("brand.tagline")} />
      <h1 className="font-heading text-3xl font-bold tracking-tight md:text-4xl">{t("browse.title")}</h1>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setCat("")}
          data-testid="browse-cat-all"
          className={`rounded-full px-4 py-2 text-sm transition-colors ${!category ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
        >
          {t("common.all")}
        </button>
        {cats?.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            data-testid={`browse-cat-${c.id}`}
            className={`rounded-full px-4 py-2 text-sm transition-colors ${category === c.id ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:text-foreground"}`}
          >
            {loc(c.name)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : videos?.length ? (
        <div className="mt-8 grid grid-cols-2 gap-4 pb-16 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} className="w-full" />
          ))}
        </div>
      ) : (
        <p className="mt-12 text-muted-foreground">{t("common.noResults")}</p>
      )}
    </div>
  );
}
